import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, Trash2, Copy, Check, Eye, EyeOff, Instagram, Building, 
  CreditCard, User, ChevronDown, ChevronUp, Pencil, Users, Building2, Shield, KeyRound
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const AdminUsers = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("artists");
  const [artists, setArtists] = useState([]);
  const [brands, setBrands] = useState([]);
  const [agencyUsers, setAgencyUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedArtist, setExpandedArtist] = useState(null);
  
  // Dialog states
  const [showCreateArtistDialog, setShowCreateArtistDialog] = useState(false);
  const [showEditArtistDialog, setShowEditArtistDialog] = useState(false);
  const [showCreateBrandDialog, setShowCreateBrandDialog] = useState(false);
  const [showCreateAgencyDialog, setShowCreateAgencyDialog] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [resetPasswordValue, setResetPasswordValue] = useState("");
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  
  // Form states
  const initialArtistForm = {
    original_name: "", alias_name: "", email: "", password: "",
    instagram_url: "", pan_number: "", bank_name: "", ifsc_code: "",
    bank_account_number: "", bank_account_type: "savings",
    payout_category: "CAT-A", s_user_id: ""
  };
  const [artistForm, setArtistForm] = useState(initialArtistForm);
  const [editArtistForm, setEditArtistForm] = useState(initialArtistForm);
  const [brandForm, setBrandForm] = useState({ name: "", email: "", password: "" });
  const [agencyForm, setAgencyForm] = useState({ name: "", email: "", password: "" });
  const [formLoading, setFormLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [artistsRes, brandsRes, agencyRes] = await Promise.all([
        axios.get(`${API}/admin/artists`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/admin/brands`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/admin/agency-users`, { headers: { Authorization: `Bearer ${token}` }})
      ]);
      setArtists(artistsRes.data);
      setBrands(brandsRes.data);
      setAgencyUsers(agencyRes.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, [token]);

  const generatePassword = (setter, form) => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%";
    let password = "";
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setter({ ...form, password });
  };

  const copyCredentials = () => {
    if (!createdCredentials) return;
    let text = `Email: ${createdCredentials.email}\nPassword: ${createdCredentials.password}`;
    if (createdCredentials.alias_name) text = `Artist: ${createdCredentials.alias_name}\nS-USER ID: ${createdCredentials.s_user_id}\n${text}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // === Artist Handlers ===
  const handleCreateArtist = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.post(`${API}/admin/artists`, artistForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreatedCredentials({ ...artistForm });
      setArtistForm(initialArtistForm);
      setShowCreateArtistDialog(false);
      fetchUsers();
      toast.success("Artist created successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create artist");
    } finally {
      setFormLoading(false);
    }
  };

  const openEditArtistDialog = (artist) => {
    setEditingArtist(artist);
    setEditArtistForm({
      original_name: artist.original_name || "",
      alias_name: artist.alias_name || artist.name || "",
      email: artist.email || "",
      password: "",
      instagram_url: artist.instagram_url || "",
      pan_number: artist.pan_number || "",
      bank_name: artist.bank_name || "",
      ifsc_code: artist.ifsc_code || "",
      bank_account_number: artist.bank_account_number || "",
      bank_account_type: artist.bank_account_type || "savings",
      payout_category: artist.payout_category || "CAT-A",
      s_user_id: artist.s_user_id || ""
    });
    setShowEditArtistDialog(true);
  };

  const handleEditArtist = async (e) => {
    e.preventDefault();
    if (!editingArtist) return;
    setFormLoading(true);
    try {
      const updateData = { ...editArtistForm };
      if (!updateData.password) delete updateData.password;
      await axios.put(`${API}/admin/artists/${editingArtist.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEditArtistDialog(false);
      setEditingArtist(null);
      fetchUsers();
      toast.success("Artist updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update artist");
    } finally {
      setFormLoading(false);
    }
  };

  // === Brand Handlers ===
  const handleCreateBrand = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.post(`${API}/admin/brands`, brandForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreatedCredentials({ ...brandForm });
      setBrandForm({ name: "", email: "", password: "" });
      setShowCreateBrandDialog(false);
      fetchUsers();
      toast.success("Brand partner created successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create brand");
    } finally {
      setFormLoading(false);
    }
  };

  // === Agency Handlers ===
  const handleCreateAgency = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.post(`${API}/admin/agency-users`, agencyForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreatedCredentials({ ...agencyForm });
      setAgencyForm({ name: "", email: "", password: "" });
      setShowCreateAgencyDialog(false);
      fetchUsers();
      toast.success("Agency user created successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create agency user");
    } finally {
      setFormLoading(false);
    }
  };

  // === Delete Handler ===
  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    try {
      let endpoint = "";
      if (deleteUser.role === "artist") endpoint = `/admin/artists/${deleteUser.id}`;
      else if (deleteUser.role === "brand") endpoint = `/admin/brands/${deleteUser.id}`;
      else if (deleteUser.role === "admin") endpoint = `/admin/agency-users/${deleteUser.id}`;
      
      await axios.delete(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleteUser(null);
    }
  };

  // === Reset Password Handler ===
  const handleResetPassword = async () => {
    if (!resetPasswordUser || !resetPasswordValue) return;
    setResetPasswordLoading(true);
    try {
      await axios.put(
        `${API}/admin/users/${resetPasswordUser.id}/reset-password`,
        { new_password: resetPasswordValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Password reset for ${resetPasswordUser.email}`);
      setResetPasswordUser(null);
      setResetPasswordValue("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetPasswordLoading(false);
    }
  };

  // === Render Functions ===
  const renderArtistsTable = () => (
    <div className="bg-white border border-gray-200 overflow-hidden">
      {artists.length === 0 ? (
        <div className="p-8 text-center text-gray-500">No artists yet. Click "Add Artist" to create one.</div>
      ) : (
        <table className="kf-table">
          <thead>
            <tr className="bg-gray-50">
              <th>S-USER ID</th>
              <th>Artist Name</th>
              <th>Category</th>
              <th>Email</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {artists.map((artist) => (
              <React.Fragment key={artist.id}>
                <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedArtist(expandedArtist === artist.id ? null : artist.id)}>
                  <td className="font-mono text-sm text-[#00B4E5] font-semibold">{artist.s_user_id || "-"}</td>
                  <td>
                    <div className="font-medium text-gray-900">{artist.alias_name || artist.name}</div>
                    {artist.original_name && artist.original_name !== artist.alias_name && (
                      <div className="text-xs text-gray-500">({artist.original_name})</div>
                    )}
                  </td>
                  <td>
                    <span className={`px-2 py-1 text-xs font-semibold rounded ${
                      artist.payout_category === 'CAT-A' ? 'bg-purple-100 text-purple-700' :
                      artist.payout_category === 'CAT-B' ? 'bg-blue-100 text-blue-700' :
                      artist.payout_category === 'CAT-C' ? 'bg-green-100 text-green-700' :
                      artist.payout_category === 'CAT-GB' ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>{artist.payout_category || "-"}</span>
                  </td>
                  <td className="text-gray-600">{artist.email}</td>
                  <td className="text-gray-500 text-sm">{new Date(artist.created_at).toLocaleDateString()}</td>
                  <td className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setExpandedArtist(expandedArtist === artist.id ? null : artist.id); }} className="text-gray-500">
                      {expandedArtist === artist.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEditArtistDialog(artist); }} className="text-[#00B4E5]">
                      <Pencil size={18} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setResetPasswordUser(artist); setResetPasswordValue(""); }} className="text-amber-600" title="Reset Password">
                      <KeyRound size={18} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setDeleteUser(artist); }} className="text-red-500">
                      <Trash2 size={18} />
                    </Button>
                  </td>
                </tr>
                {expandedArtist === artist.id && (
                  <tr className="bg-gray-50">
                    <td colSpan="6" className="p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div><span className="text-gray-500 block">Instagram</span>
                          {artist.instagram_url ? <a href={artist.instagram_url} target="_blank" rel="noopener noreferrer" className="text-[#00B4E5] hover:underline flex items-center gap-1"><Instagram size={14} /> View Profile</a> : "-"}
                        </div>
                        <div><span className="text-gray-500 block">PAN Number</span><span className="font-mono">{artist.pan_number || "-"}</span></div>
                        <div><span className="text-gray-500 block">Bank</span><span>{artist.bank_name || "-"}</span></div>
                        <div><span className="text-gray-500 block">IFSC Code</span><span className="font-mono">{artist.ifsc_code || "-"}</span></div>
                        <div><span className="text-gray-500 block">Account Number</span><span className="font-mono">{artist.bank_account_number ? `****${artist.bank_account_number.slice(-4)}` : "-"}</span></div>
                        <div><span className="text-gray-500 block">Account Type</span><span className="capitalize">{artist.bank_account_type || "-"}</span></div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  const renderBrandsTable = () => (
    <div className="bg-white border border-gray-200 overflow-hidden">
      {brands.length === 0 ? (
        <div className="p-8 text-center text-gray-500">No brand partners yet. Click "Add Brand" to create one.</div>
      ) : (
        <table className="kf-table">
          <thead>
            <tr className="bg-gray-50">
              <th>Name</th>
              <th>Email</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {brands.map((brand) => (
              <tr key={brand.id} className="hover:bg-gray-50">
                <td className="font-medium text-gray-900">{brand.name}</td>
                <td className="text-gray-600">{brand.email}</td>
                <td className="text-gray-500 text-sm">{new Date(brand.created_at).toLocaleDateString()}</td>
                <td className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => { setResetPasswordUser(brand); setResetPasswordValue(""); }} className="text-amber-600" title="Reset Password">
                    <KeyRound size={18} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteUser(brand)} className="text-red-500">
                    <Trash2 size={18} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  const renderAgencyTable = () => (
    <div className="bg-white border border-gray-200 overflow-hidden">
      {agencyUsers.length === 0 ? (
        <div className="p-8 text-center text-gray-500">No additional agency users. Click "Add Agency User" to create one.</div>
      ) : (
        <table className="kf-table">
          <thead>
            <tr className="bg-gray-50">
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {agencyUsers.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="font-medium text-gray-900">{user.name}</td>
                <td className="text-gray-600">{user.email}</td>
                <td>
                  {user.is_owner ? (
                    <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-100 text-yellow-700">Owner</span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-700">Admin</span>
                  )}
                </td>
                <td className="text-gray-500 text-sm">{new Date(user.created_at).toLocaleDateString()}</td>
                <td>
                  {!user.is_owner && (
                    <Button variant="ghost" size="sm" onClick={() => setDeleteUser(user)} className="text-red-500">
                      <Trash2 size={18} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-users">
        <div className="mb-6">
          <h1 className="text-4xl font-heading text-gray-900 mb-2">USER MANAGEMENT</h1>
          <p className="text-gray-600">Manage all users across Artists, Brands, and Agency</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 bg-gray-100">
            <TabsTrigger value="artists" className="flex items-center gap-2 data-[state=active]:bg-white">
              <Users size={18} /> Artists ({artists.length})
            </TabsTrigger>
            <TabsTrigger value="brands" className="flex items-center gap-2 data-[state=active]:bg-white">
              <Building2 size={18} /> Brands ({brands.length})
            </TabsTrigger>
            <TabsTrigger value="agency" className="flex items-center gap-2 data-[state=active]:bg-white">
              <Shield size={18} /> Agency ({agencyUsers.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="artists" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setShowCreateArtistDialog(true)} className="kf-btn-primary">
                <Plus size={18} className="mr-2" /> Add Artist
              </Button>
            </div>
            {loading ? <div className="p-8 text-center text-gray-500">Loading...</div> : renderArtistsTable()}
          </TabsContent>

          <TabsContent value="brands" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setShowCreateBrandDialog(true)} className="kf-btn-primary">
                <Plus size={18} className="mr-2" /> Add Brand
              </Button>
            </div>
            {loading ? <div className="p-8 text-center text-gray-500">Loading...</div> : renderBrandsTable()}
          </TabsContent>

          <TabsContent value="agency" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setShowCreateAgencyDialog(true)} className="kf-btn-primary">
                <Plus size={18} className="mr-2" /> Add Agency User
              </Button>
            </div>
            {loading ? <div className="p-8 text-center text-gray-500">Loading...</div> : renderAgencyTable()}
          </TabsContent>
        </Tabs>

        {/* Create Artist Dialog */}
        <Dialog open={showCreateArtistDialog} onOpenChange={setShowCreateArtistDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">CREATE ARTIST</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateArtist} className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2 border-b pb-2"><User size={18} /> Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Original Name (as per Bank/Govt records) *</Label>
                    <Input value={artistForm.original_name} onChange={(e) => setArtistForm({ ...artistForm, original_name: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Artist Alias / Stage Name *</Label>
                    <Input value={artistForm.alias_name} onChange={(e) => setArtistForm({ ...artistForm, alias_name: e.target.value })} required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>S-USER ID (Unique) *</Label>
                    <Input value={artistForm.s_user_id} onChange={(e) => setArtistForm({ ...artistForm, s_user_id: e.target.value.toUpperCase() })} required className="font-mono" />
                  </div>
                  <div className="space-y-2">
                    <Label>Instagram Profile URL</Label>
                    <Input value={artistForm.instagram_url} onChange={(e) => setArtistForm({ ...artistForm, instagram_url: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Payout Category *</Label>
                  <Select value={artistForm.payout_category} onValueChange={(v) => setArtistForm({ ...artistForm, payout_category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CAT-A">CAT-A</SelectItem>
                      <SelectItem value="CAT-B">CAT-B</SelectItem>
                      <SelectItem value="CAT-C">CAT-C</SelectItem>
                      <SelectItem value="CAT-GB">CAT-GB</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2 border-b pb-2"><Building size={18} /> Bank Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>PAN Card Number *</Label><Input value={artistForm.pan_number} onChange={(e) => setArtistForm({ ...artistForm, pan_number: e.target.value.toUpperCase() })} required maxLength={10} className="font-mono uppercase" /></div>
                  <div className="space-y-2"><Label>Bank Name *</Label><Input value={artistForm.bank_name} onChange={(e) => setArtistForm({ ...artistForm, bank_name: e.target.value })} required /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>IFSC Code *</Label><Input value={artistForm.ifsc_code} onChange={(e) => setArtistForm({ ...artistForm, ifsc_code: e.target.value.toUpperCase() })} required maxLength={11} className="font-mono uppercase" /></div>
                  <div className="space-y-2"><Label>Bank Account Number *</Label><Input value={artistForm.bank_account_number} onChange={(e) => setArtistForm({ ...artistForm, bank_account_number: e.target.value })} required className="font-mono" /></div>
                </div>
                <div className="space-y-2">
                  <Label>Account Type *</Label>
                  <Select value={artistForm.bank_account_type} onValueChange={(v) => setArtistForm({ ...artistForm, bank_account_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="savings">Savings Account</SelectItem>
                      <SelectItem value="current">Current Account</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2 border-b pb-2"><CreditCard size={18} /> Login Credentials</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Email Address *</Label><Input type="email" value={artistForm.email} onChange={(e) => setArtistForm({ ...artistForm, email: e.target.value })} required /></div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Password *</Label>
                      <Button type="button" variant="link" size="sm" onClick={() => generatePassword(setArtistForm, artistForm)} className="text-[#00B4E5] p-0 h-auto">Generate</Button>
                    </div>
                    <div className="relative">
                      <Input type={showPassword ? "text" : "password"} value={artistForm.password} onChange={(e) => setArtistForm({ ...artistForm, password: e.target.value })} required className="pr-10" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateArtistDialog(false)}>Cancel</Button>
                <Button type="submit" className="kf-btn-primary" disabled={formLoading}>{formLoading ? "Creating..." : "Create Artist"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Artist Dialog */}
        <Dialog open={showEditArtistDialog} onOpenChange={setShowEditArtistDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">EDIT ARTIST</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditArtist} className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2 border-b pb-2"><User size={18} /> Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Original Name *</Label><Input value={editArtistForm.original_name} onChange={(e) => setEditArtistForm({ ...editArtistForm, original_name: e.target.value })} required /></div>
                  <div className="space-y-2"><Label>Artist Alias *</Label><Input value={editArtistForm.alias_name} onChange={(e) => setEditArtistForm({ ...editArtistForm, alias_name: e.target.value })} required /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>S-USER ID *</Label><Input value={editArtistForm.s_user_id} onChange={(e) => setEditArtistForm({ ...editArtistForm, s_user_id: e.target.value.toUpperCase() })} required className="font-mono" /></div>
                  <div className="space-y-2"><Label>Instagram URL</Label><Input value={editArtistForm.instagram_url} onChange={(e) => setEditArtistForm({ ...editArtistForm, instagram_url: e.target.value })} /></div>
                </div>
                <div className="space-y-2">
                  <Label>Payout Category *</Label>
                  <Select value={editArtistForm.payout_category} onValueChange={(v) => setEditArtistForm({ ...editArtistForm, payout_category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CAT-A">CAT-A</SelectItem>
                      <SelectItem value="CAT-B">CAT-B</SelectItem>
                      <SelectItem value="CAT-C">CAT-C</SelectItem>
                      <SelectItem value="CAT-GB">CAT-GB</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2 border-b pb-2"><Building size={18} /> Bank Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>PAN *</Label><Input value={editArtistForm.pan_number} onChange={(e) => setEditArtistForm({ ...editArtistForm, pan_number: e.target.value.toUpperCase() })} required maxLength={10} className="font-mono" /></div>
                  <div className="space-y-2"><Label>Bank Name *</Label><Input value={editArtistForm.bank_name} onChange={(e) => setEditArtistForm({ ...editArtistForm, bank_name: e.target.value })} required /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>IFSC *</Label><Input value={editArtistForm.ifsc_code} onChange={(e) => setEditArtistForm({ ...editArtistForm, ifsc_code: e.target.value.toUpperCase() })} required maxLength={11} className="font-mono" /></div>
                  <div className="space-y-2"><Label>Account Number *</Label><Input value={editArtistForm.bank_account_number} onChange={(e) => setEditArtistForm({ ...editArtistForm, bank_account_number: e.target.value })} required className="font-mono" /></div>
                </div>
                <div className="space-y-2">
                  <Label>Account Type *</Label>
                  <Select value={editArtistForm.bank_account_type} onValueChange={(v) => setEditArtistForm({ ...editArtistForm, bank_account_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="savings">Savings Account</SelectItem>
                      <SelectItem value="current">Current Account</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2 border-b pb-2"><CreditCard size={18} /> Login Credentials</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Email *</Label><Input type="email" value={editArtistForm.email} onChange={(e) => setEditArtistForm({ ...editArtistForm, email: e.target.value })} required /></div>
                  <div className="space-y-2">
                    <Label>New Password <span className="text-gray-400 font-normal">(leave blank to keep current)</span></Label>
                    <Input type="password" value={editArtistForm.password} onChange={(e) => setEditArtistForm({ ...editArtistForm, password: e.target.value })} placeholder="Enter new password (optional)" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEditArtistDialog(false)}>Cancel</Button>
                <Button type="submit" className="kf-btn-primary" disabled={formLoading}>{formLoading ? "Saving..." : "Save Changes"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create Brand Dialog */}
        <Dialog open={showCreateBrandDialog} onOpenChange={setShowCreateBrandDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">CREATE BRAND PARTNER</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateBrand} className="space-y-4">
              <div className="space-y-2"><Label>Brand Name *</Label><Input value={brandForm.name} onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })} required /></div>
              <div className="space-y-2"><Label>Email Address *</Label><Input type="email" value={brandForm.email} onChange={(e) => setBrandForm({ ...brandForm, email: e.target.value })} required /></div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Password *</Label>
                  <Button type="button" variant="link" size="sm" onClick={() => generatePassword(setBrandForm, brandForm)} className="text-[#00B4E5] p-0 h-auto">Generate</Button>
                </div>
                <Input type="password" value={brandForm.password} onChange={(e) => setBrandForm({ ...brandForm, password: e.target.value })} required />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateBrandDialog(false)}>Cancel</Button>
                <Button type="submit" className="kf-btn-primary" disabled={formLoading}>{formLoading ? "Creating..." : "Create Brand"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create Agency User Dialog */}
        <Dialog open={showCreateAgencyDialog} onOpenChange={setShowCreateAgencyDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">CREATE AGENCY USER</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateAgency} className="space-y-4">
              <div className="space-y-2"><Label>Full Name *</Label><Input value={agencyForm.name} onChange={(e) => setAgencyForm({ ...agencyForm, name: e.target.value })} required /></div>
              <div className="space-y-2"><Label>Email Address *</Label><Input type="email" value={agencyForm.email} onChange={(e) => setAgencyForm({ ...agencyForm, email: e.target.value })} required /></div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Password *</Label>
                  <Button type="button" variant="link" size="sm" onClick={() => generatePassword(setAgencyForm, agencyForm)} className="text-[#00B4E5] p-0 h-auto">Generate</Button>
                </div>
                <Input type="password" value={agencyForm.password} onChange={(e) => setAgencyForm({ ...agencyForm, password: e.target.value })} required />
              </div>
              <p className="text-sm text-gray-500">This user will have admin access to the Agency dashboard but cannot manage other users.</p>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateAgencyDialog(false)}>Cancel</Button>
                <Button type="submit" className="kf-btn-primary" disabled={formLoading}>{formLoading ? "Creating..." : "Create User"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Credentials Dialog */}
        <Dialog open={!!createdCredentials} onOpenChange={() => setCreatedCredentials(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">USER CREATED</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-600">Share these credentials with the user. The password will not be shown again.</p>
              <div className="bg-gray-100 p-4 font-mono text-sm space-y-1">
                {createdCredentials?.alias_name && <p><strong>Artist:</strong> {createdCredentials.alias_name}</p>}
                {createdCredentials?.s_user_id && <p><strong>S-USER ID:</strong> {createdCredentials.s_user_id}</p>}
                {createdCredentials?.name && !createdCredentials?.alias_name && <p><strong>Name:</strong> {createdCredentials.name}</p>}
                <p><strong>Email:</strong> {createdCredentials?.email}</p>
                <p><strong>Password:</strong> {createdCredentials?.password}</p>
              </div>
              <Button onClick={copyCredentials} variant="outline" className="w-full flex items-center justify-center gap-2">
                {copied ? <Check size={18} /> : <Copy size={18} />} {copied ? "Copied!" : "Copy Credentials"}
              </Button>
            </div>
            <DialogFooter>
              <Button onClick={() => setCreatedCredentials(null)} className="kf-btn-primary">Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete User</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{deleteUser?.alias_name || deleteUser?.name}</strong>? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteUser} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reset Password Dialog */}
        <Dialog open={!!resetPasswordUser} onOpenChange={() => setResetPasswordUser(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">RESET PASSWORD</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-600">
                Reset password for <strong>{resetPasswordUser?.alias_name || resetPasswordUser?.name || resetPasswordUser?.email}</strong>
              </p>
              <div className="space-y-2">
                <Label>New Password *</Label>
                <div className="flex gap-2">
                  <Input 
                    type="text" 
                    value={resetPasswordValue} 
                    onChange={(e) => setResetPasswordValue(e.target.value)}
                    placeholder="Enter new password"
                    data-testid="reset-password-input"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%";
                      let pw = "";
                      for (let i = 0; i < 12; i++) pw += chars.charAt(Math.floor(Math.random() * chars.length));
                      setResetPasswordValue(pw);
                    }}
                    className="text-[#00B4E5] whitespace-nowrap"
                  >
                    Generate
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetPasswordUser(null)}>Cancel</Button>
              <Button 
                onClick={handleResetPassword} 
                className="kf-btn-primary" 
                disabled={!resetPasswordValue || resetPasswordLoading}
                data-testid="reset-password-confirm"
              >
                {resetPasswordLoading ? "Resetting..." : "Reset Password"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
